import type { AnchoredPopupWidth } from "../AnchoredPopup";
import type { TextVariantDefinition } from "../Text";
import { controlPadX, controlPadY, inlineElementTypography } from "../foundations/controlStyles";

export type DropdownMenuWidth = "matchTrigger" | (string & {});
export type DropdownMode = "facet" | "field";

export function resolveDropdownMenuWidth(menuWidth: DropdownMenuWidth): AnchoredPopupWidth {
  return menuWidth === "matchTrigger" ? "matchAnchor" : menuWidth;
}

export type DropdownTriggerSize = "sm" | "md";

// Single/MultiSelect 트리거가 공유하는 size 별 밀도. 세로 패딩은 inlineElementHeight(sm 28 / md 32) 이하로
// 둬 min-height 가 높이를 잡게 한다. 좌우는 sm 만 controlPadX 를 공유하고 md 는 자체 값이다 —
// 트리거 md 라벨은 body 라 시각 상하 여백(6.25px)이 caption 라벨 버튼보다 낮아, 같은 비율(≈2.2)에
// 14px 로 충분하다(버튼 md 의 16px 를 따라가면 과대).
// 세로: md 는 controlPadY 를 공유하지만 sm 은 0.2rem 자체 값이다 — MultiSelect 커스텀 트리거가
// 고정 height 셸에서 이 패딩을 먹어(가용 높이 = 28px − 2×padY − 테두리) controlPadY.sm(0.3rem)
// 이면 칩(LabelChip) 콘텐츠가 잘린다. 라벨-only 트리거에선 어느 쪽이든 no-op(min-height 바인딩).
const triggerDensity = {
  sm: { padY: "0.2rem", padX: controlPadX.sm, gap: "0.35rem" },
  md: { padY: controlPadY.md, padX: "0.875rem", gap: "0.5rem" },
} as const satisfies Record<DropdownTriggerSize, { padY: string; padX: string; gap: string }>;

export const dropdownTriggerPadding = (size: DropdownTriggerSize): string =>
  `${triggerDensity[size].padY} ${triggerDensity[size].padX}`;

export const dropdownTriggerGap = (size: DropdownTriggerSize): string => triggerDensity[size].gap;

const triggerTextStyleBase = {
  display: "block",
  minWidth: 0,
  overflow: "hidden",
  color: "inherit",
  fontWeight: "var(--c6-typography-weight-regular)",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

// 트리거 라벨 타이포 — size 별 font-size 만 다르다(sm caption / md body). facet/field 모두 regular 로
// 두고 의미 차이는 label 구성과 selected surface 로 표현한다.
export const dropdownTriggerTextVariant = {
  sm: {
    as: "span",
    style: { ...triggerTextStyleBase, ...inlineElementTypography.sm },
  },
  md: {
    as: "span",
    style: { ...triggerTextStyleBase, ...inlineElementTypography.md },
  },
} satisfies Record<DropdownTriggerSize, TextVariantDefinition>;
